import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiEdit2, FiRotateCcw, FiUsers } from 'react-icons/fi';
import Attachment from './attachment';
import { TagEditList } from '../common/tag_edit_list';
import ShareAccessModal from '../common/share_access_modal';
import VaultRichEditor, {
  isEmptyRichHtml,
  sanitizeVaultHtml,
} from './vault_rich_editor';

const AUTOSAVE_MS = 500;
const SESSION_PREFIX = 'walmates-vault-edit:';

function sessionKey(vaultId) {
  const userId = localStorage.getItem('walmates-user_id') || 'anon';
  return `${SESSION_PREFIX}${userId}:${vaultId}`;
}

function readSession(vaultId) {
  try {
    const raw = sessionStorage.getItem(sessionKey(vaultId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(vaultId, payload) {
  try {
    sessionStorage.setItem(sessionKey(vaultId), JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

function clearSession(vaultId) {
  try {
    sessionStorage.removeItem(sessionKey(vaultId));
  } catch {
    /* ignore */
  }
}

/** Normalize stored note body into Quill-friendly HTML. */
function toEditorHtml(html) {
  if (!html) return '';
  const raw = String(html);
  // Legacy plain notes may only have newlines / <br>.
  if (!/<[a-z][\s\S]*>/i.test(raw) || /^([^<]|<br\s*\/?>)*$/i.test(raw.trim())) {
    return raw
      .split(/\n|<br\s*\/?>/gi)
      .map((line) => `<p>${line || '<br>'}</p>`)
      .join('');
  }
  return raw;
}

/**
 * Premium note detail: view mode by default; edit via pencil;
 * debounced autosave + undo; draft survives tab switches via sessionStorage.
 */
const VaultNoteDetail = ({
  note,
  onUpdateNote,
  onFileUpload,
  onFileRemove,
  pendingUploads,
  suggestedTags = [],
  onShareSaved,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle | dirty | saving | saved
  const [showShare, setShowShare] = useState(false);
  const saveTimer = useRef(null);
  const titleRef = useRef(null);
  const latestDrafts = useRef({ title: '', body: '' });
  const vaultId = note?.vault_id;

  const flushSave = useCallback(
    (title, body) => {
      if (!vaultId) return;
      setSaveState('saving');
      onUpdateNote(vaultId, 'title', title);
      onUpdateNote(vaultId, 'data', sanitizeVaultHtml(body));
      writeSession(vaultId, {
        isEditing: true,
        titleDraft: title,
        bodyDraft: body,
        snapshot,
      });
      setSaveState('saved');
      window.setTimeout(() => {
        setSaveState((prev) => (prev === 'saved' ? 'idle' : prev));
      }, 1200);
    },
    [vaultId, onUpdateNote, snapshot]
  );

  const scheduleSave = useCallback(
    (title, body) => {
      latestDrafts.current = { title, body };
      setSaveState('dirty');
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        flushSave(title, body);
      }, AUTOSAVE_MS);
    },
    [flushSave]
  );

  const enterEdit = useCallback(() => {
    if (!note) return;
    const session = readSession(note.vault_id);
    const title =
      session?.titleDraft != null ? session.titleDraft : note.title || '';
    const body =
      session?.bodyDraft != null
        ? session.bodyDraft
        : toEditorHtml(note.data || '');
    const snap =
      session?.snapshot ||
      { title: note.title || '', body: toEditorHtml(note.data || '') };

    setSnapshot(snap);
    setTitleDraft(title);
    setBodyDraft(body);
    latestDrafts.current = { title, body };
    setIsEditing(true);
    writeSession(note.vault_id, {
      isEditing: true,
      titleDraft: title,
      bodyDraft: body,
      snapshot: snap,
    });
    requestAnimationFrame(() => titleRef.current?.focus());
  }, [note]);

  const exitEdit = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const { title, body } = latestDrafts.current;
    if (isEditing && vaultId) {
      flushSave(title, body);
      clearSession(vaultId);
    }
    setIsEditing(false);
    setSaveState('idle');
  }, [flushSave, isEditing, vaultId]);

  const handleUndo = useCallback(() => {
    if (!snapshot) return;
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    setTitleDraft(snapshot.title);
    setBodyDraft(snapshot.body);
    latestDrafts.current = { title: snapshot.title, body: snapshot.body };
    flushSave(snapshot.title, snapshot.body);
  }, [snapshot, flushSave]);

  // Restore edit session when selecting a note (or remounting after tab switch).
  useEffect(() => {
    if (!vaultId) return undefined;

    // Flush previous note's pending autosave when switching selection.
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
    };
  }, [vaultId]);

  useEffect(() => {
    if (!vaultId || !note) return;
    const session = readSession(vaultId);
    if (session?.isEditing) {
      setSnapshot(
        session.snapshot || {
          title: note.title || '',
          body: toEditorHtml(note.data || ''),
        }
      );
      setTitleDraft(session.titleDraft ?? '');
      setBodyDraft(session.bodyDraft ?? '');
      latestDrafts.current = {
        title: session.titleDraft ?? '',
        body: session.bodyDraft ?? '',
      };
      setIsEditing(true);
    } else {
      setIsEditing(false);
      setTitleDraft(note.title || '');
      setBodyDraft(toEditorHtml(note.data || ''));
      setSnapshot(null);
      setSaveState('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultId]);

  // Persist draft + flush on tab hide.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden' || !isEditing || !vaultId) return;
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      const { title, body } = latestDrafts.current;
      writeSession(vaultId, {
        isEditing: true,
        titleDraft: title,
        bodyDraft: body,
        snapshot,
      });
      flushSave(title, body);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isEditing, vaultId, snapshot, flushSave]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        exitEdit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEditing, exitEdit]);

  const canUndo = useMemo(() => {
    if (!snapshot || !isEditing) return false;
    return titleDraft !== snapshot.title || bodyDraft !== snapshot.body;
  }, [snapshot, isEditing, titleDraft, bodyDraft]);

  const previewBody = note?.data || '';
  const emptyBody = isEmptyRichHtml(note?.data || '');

  if (!note) return null;

  return (
    <article className={`vault-detail fade-in${isEditing ? ' is-editing' : ''}`}>
      <header className="vault-detail-header">
        {isEditing ? (
          <input
            ref={titleRef}
            className="vault-detail-title-input"
            value={titleDraft}
            placeholder="Untitled"
            onChange={(e) => {
              const next = e.target.value;
              setTitleDraft(next);
              scheduleSave(next, bodyDraft);
            }}
          />
        ) : (
          <h1 className="vault-detail-title">{note.title || 'Untitled'}</h1>
        )}

        <div className="vault-detail-actions">
          {isEditing ? (
            <>
              <span
                className={`vault-save-status vault-save-status--${saveState}`}
                aria-live="polite"
              >
                {saveState === 'dirty' && 'Editing…'}
                {saveState === 'saving' && 'Saving…'}
                {saveState === 'saved' && 'Saved'}
              </span>
              <button
                type="button"
                className="vault-icon-btn"
                title="Undo changes"
                disabled={!canUndo}
                onClick={handleUndo}
              >
                <FiRotateCcw />
              </button>
              <button
                type="button"
                className="vault-icon-btn vault-icon-btn--primary"
                title="Done editing"
                onClick={exitEdit}
              >
                <FiCheck />
              </button>
            </>
          ) : (
            <>
              {note.can_manage_sharing && (
                <button type="button" className="vault-icon-btn" title="Share note" onClick={() => setShowShare(true)}>
                  <FiUsers />
                </button>
              )}
              <button
                type="button"
                className="vault-icon-btn vault-icon-btn--primary"
                title="Edit note"
                onClick={enterEdit}
              >
                <FiEdit2 />
              </button>
            </>
          )}
        </div>
      </header>

      {note.is_shared && note.owner_name && (
        <div className="vault-shared-label">Shared by {note.owner_name}</div>
      )}

      <section className="vault-detail-body">
        {isEditing ? (
          <VaultRichEditor
            className="vault-detail-body-editor"
            value={bodyDraft}
            placeholder="Write the details of this note…"
            onChange={(next) => {
              setBodyDraft(next);
              scheduleSave(titleDraft, next);
            }}
          />
        ) : emptyBody ? (
          <p className="vault-detail-body-empty">No details yet. Tap edit to add notes.</p>
        ) : (
          <div
            className="vault-detail-body-html"
            dangerouslySetInnerHTML={{ __html: previewBody }}
          />
        )}
      </section>

      <Attachment
        note={note}
        pendingUploads={pendingUploads}
        onFileUpload={onFileUpload}
        onFileRemove={onFileRemove}
      />
      {showShare && (
        <ShareAccessModal
          resource="vault"
          itemId={note.vault_id}
          title={note.title || 'Untitled'}
          onClose={() => setShowShare(false)}
          onSaved={onShareSaved}
        />
      )}

      <section className="vault-detail-tags">
        <TagEditList
          item={note}
          hearts={false}
          suggestedTags={suggestedTags}
          onTagChange={(newTag) => onUpdateNote(note.vault_id, 'tags', newTag)}
        />
      </section>
    </article>
  );
};

export default VaultNoteDetail;
